import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import Layout from './components/Layout';
import RouteFallback from './components/RouteFallback';
import SeoHead from './components/SeoHead';
import ErrorBoundary from './components/ErrorBoundary';
import { StalePreloadHealthBeacon } from './components/StalePreloadHealthBeacon';

// KAN-215: Route-level code splitting — keeps initial bundle small; editor/fabric/three load on demand.
const HomePage = lazy(() => import('./pages/HomePage'));
const CaseSelectorPage = lazy(() => import('./pages/CaseSelectorPage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const IndustriesPage = lazy(() => import('./pages/IndustriesPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const FoamGuidePage = lazy(() => import('./pages/FoamGuidePage'));
const TutorialSlideshowPage = lazy(() => import('./pages/TutorialSlideshowPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const FaqPage = lazy(() => import('./pages/FaqPage'));
const GalleryPage = lazy(() => import('./pages/GalleryPage'));
const PremadeCasesPage = lazy(() => import('./pages/PremadeCasesPage'));
// KAN-SEO-004: /premade-cases/:slug — category landings or case detail pages
const PremadeCaseSlugPage = lazy(() => import('./pages/PremadeCaseSlugPage'));
const ComparePage = lazy(() => import('./pages/ComparePage'));
const CaseMakersPage = lazy(() => import('./pages/CaseMakersPage'));
// KAN-COMP-PRODESIGN-001: paid expert-design service landing page (/pro-design)
const ProDesignPage = lazy(() => import('./pages/ProDesignPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const EditorPage = lazy(() => import('./pages/EditorPage'));
const DesignsPage = lazy(() => import('./pages/DesignsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const QuotePage = lazy(() => import('./pages/QuotePage'));
const SharePage = lazy(() => import('./pages/SharePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

function App() {
  return (
    <>
      <SeoHead />
      {/* #263: route-level error boundary so a single component throw (e.g. a bad
          router hook) can never unmount the whole tree into a blank page again. */}
      <ErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
      {/* GH#914: inside Suspense on purpose — mounts only once a route chunk
          has resolved, which is the only safe moment to reset the one-shot
          stale-chunk reload marker. */}
      <StalePreloadHealthBeacon />
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="products" element={<ProductsPage />} />
          <Route path="industries" element={<IndustriesPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="foam-guide" element={<FoamGuidePage />} />
          <Route path="tutorial" element={<TutorialSlideshowPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="faq" element={<FaqPage />} />
          <Route path="gallery" element={<GalleryPage />} />
          <Route path="premade-cases" element={<PremadeCasesPage />} />
          <Route path="premade-cases/:slug" element={<PremadeCaseSlugPage />} />
          <Route path="compare" element={<ComparePage />} />
          <Route path="for-case-makers" element={<CaseMakersPage />} />
          <Route path="pro-design" element={<ProDesignPage />} />
          <Route path="blog" element={<BlogPage />} />
          <Route path="blog/:slug" element={<BlogPostPage />} />
          <Route path="shop" element={<ShopPage />} />
          <Route path="design" element={<CaseSelectorPage />} />
          <Route path="editor" element={<EditorPage />} />
          <Route path="editor/:designId" element={<EditorPage />} />
          <Route path="designs" element={<DesignsPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="quote/:designId" element={<QuotePage />} />
          <Route path="share/:token" element={<SharePage />} />
          {/* #67: catch-all 404 inside Layout so unknown routes show a 404 page
              (with header/nav) instead of a blank screen. */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route path="/login" element={<LoginPage />} />
      </Routes>
      </Suspense>
      </ErrorBoundary>
    </>
  );
}

export default App;
